/**
 * Stats section (campaign tabs + sub-tabs)
 * For Media Devoted | Created By Ciro Autiero
 *
 * Tag fetches are deferred so drop stats load first (useStatsPerfTags). The tabs
 * read the active group's tracker through TrackerStatsScope.
 */

import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Link2, PinOff, X } from 'lucide-react';

import { Button, LoadingSpinner } from '@/components/ui';
import { SmswiserCampaignPicker } from '@/features/smswiser-campaigns/components/shared';
import { DeployedDropsTab } from '@/features/smswiser-campaigns/components/DeployedDropsTab/DeployedDropsTab';
import { GatewayStatsTab } from '@/features/smswiser-campaigns/components/GatewayStatsTab/GatewayStatsTab';
import { GroupFlowTab } from '@/features/smswiser-campaigns/components/GroupFlowTab';
import { LandersStatsTab } from '@/features/smswiser-campaigns/components/LandersStatsTab/LandersStatsTab';
import { ListsStatsTab } from '@/features/smswiser-campaigns/components/ListsStatsTab/ListsStatsTab';
import { OffersStatsTab } from '@/features/smswiser-campaigns/components/OffersStatsTab/OffersStatsTab';
import { PathsStatsTab } from '@/features/smswiser-campaigns/components/PathsStatsTab/PathsStatsTab';
import { SchedulerTab } from '@/features/smswiser-campaigns/components/SchedulerTab';
import { AutomationTab } from '@/features/smswiser-campaigns/components/AutomationTab/AutomationTab';
import SmswiserCampaignStatsTab from '@/features/smswiser-campaigns/components/SmswiserCampaignStatsTab';
import { SourcesStatsTab } from '@/features/smswiser-campaigns/components/SourcesStatsTab/SourcesStatsTab';
import { TextsStatsTab } from '@/features/smswiser-campaigns/components/TextsStatsTab/TextsStatsTab';
import { TrackerStatsScope, statsSourceLabel } from '@/features/trackers';
import { useAuth } from '@/hooks';
import { cn } from '@/lib/utils';
import { PhoneStatsTab } from '@/features/smswiser-campaigns/components/PhoneStatsTab';
import {
  StatsSourceToggle,
  ToolStatsTab,
} from '@/features/smswiser-campaigns/components/ToolStatsTab';

import { STATS_SUB_TABS } from '../constants';
import { statsSubTabsFor } from '../tracker';
import { trafficCampaignGroupKey } from './stats-traffic';
import { StatsSubTabBar } from './StatsSubTabBar';
import { useStatsPerfTags } from './useStatsPerfTags';
import type { StatsSectionProps } from '../types';

export const StatsSection: FC<StatsSectionProps> = ({
  countryKey,
  campaigns,
  tabGroups,
  activeStatsTabIndex,
  workDate,
  serverTitlePrefix,
  onAddCampaign,
  onEditTrafficLink,
  onRemoveTabGroup,
  onTabChange,
  onPinGroup,
  onUnpinGroup,
  pinnedWiserIds,
  isLoading,
}) => {
  const { hasPermission } = useAuth();
  const phoneOnly =
    hasPermission('stats.phone') && !hasPermission('drops.view');
  const canEditTraffic = hasPermission('drops.update');
  // The Scheduler tab embeds the flow-optimizer control center (same gate as /scheduler).
  const canViewScheduler = hasPermission('flows.view');
  // The Automation tab embeds the Drop Generator of the open campaign group (same gate as /admin/drop-generator).
  const canViewAutomation = hasPermission('drop_generator.view');
  const defaultTab = phoneOnly ? 'phone' : 'drops';

  const [searchParams, setSearchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') as (typeof STATS_SUB_TABS)[number] | null;
  const initialTab =
    tabFromUrl && STATS_SUB_TABS.includes(tabFromUrl)
      ? phoneOnly
        ? 'phone'
        : tabFromUrl
      : defaultTab;
  const [statsSubTab, setStatsSubTab] = useState(initialTab);

  // Sync sub-tab from URL only when URL changes — avoids reverting the active tab if state
  // updates before `useSearchParams` reflects the new `tab=` after a click.
  useEffect(() => {
    if (phoneOnly) {
      setStatsSubTab((prev) => (prev === 'phone' ? prev : 'phone'));
      return;
    }
    if (tabFromUrl && STATS_SUB_TABS.includes(tabFromUrl)) {
      setStatsSubTab(tabFromUrl);
    }
  }, [phoneOnly, tabFromUrl]);

  const setStatsSubTabWithUrl = useCallback(
    (tab: (typeof STATS_SUB_TABS)[number]) => {
      setStatsSubTab(tab);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', tab);
        return next;
      });
    },
    [setSearchParams],
  );

  // Stats source (tracker = the group's tracker, tool = SMSWiser native). URL-synced via ?src.
  const statsSource = phoneOnly || searchParams.get('src') !== 'tool' ? 'tracker' : 'tool';
  const setStatsSource = useCallback(
    (src: string) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (src === 'tool') next.set('src', 'tool');
        else next.delete('src');
        return next;
      });
    },
    [setSearchParams],
  );

  const activeGroupForProvider = tabGroups[activeStatsTabIndex];
  const activeTrafficProvider = activeGroupForProvider?.trafficProvider ?? 'VOLUUM';

  // A sub-tab the active group's tracker does not have (e.g. Flow on Tres Commas) → Drops.
  useEffect(() => {
    if (statsSubTabsFor(activeTrafficProvider).has(statsSubTab)) return;
    setStatsSubTabWithUrl('drops');
  }, [activeTrafficProvider, statsSubTab, setStatsSubTabWithUrl]);

  const selectedIds = useMemo(
    () => tabGroups.flatMap((g) => g.wiserIds),
    [tabGroups],
  );

  const perfTagsQuery = useStatsPerfTags(countryKey, tabGroups[activeStatsTabIndex]);

  const availableGroupsToAdd = useMemo(() => {
    const selectedSet = new Set(selectedIds);
    const groups: Array<{ id: number; name: string; fullLabel?: string; wiserIds: number[] }> = [];
    const seen = new Set<string>();
    for (const c of campaigns) {
      if (selectedSet.has(c.id)) continue;
      const groupKey = trafficCampaignGroupKey(c);
      if (seen.has(groupKey)) continue;
      seen.add(groupKey);
      const sameGroup = campaigns.filter((x) => trafficCampaignGroupKey(x) === groupKey);
      const fullLabel =
        sameGroup.length > 1 ? sameGroup.map((x) => x.name).join(', ') : sameGroup[0]!.name;
      const label =
        sameGroup.length > 2 ? `${sameGroup[0]!.name} (+${sameGroup.length - 1} more)` : fullLabel;
      groups.push({ id: sameGroup[0]!.id, name: label, fullLabel, wiserIds: sameGroup.map((x) => x.id) });
    }
    return groups;
  }, [campaigns, selectedIds]);

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (tabGroups.length === 0) {
    return (
      <div className="flex min-h-[320px] flex-1 flex-col items-center justify-center gap-6 rounded-lg border border-dashed border-border bg-muted/5 p-8">
        <div className="text-center space-y-1">
          <p className="text-sm font-semibold text-foreground">Drops</p>
          <p className="text-xs text-muted-foreground max-w-sm">
            Add a campaign to view stats — campaigns sharing the same tracker link are grouped automatically
          </p>
        </div>
        {availableGroupsToAdd.length > 0 ? (
          <SmswiserCampaignPicker
            countryKey={countryKey}
            campaigns={availableGroupsToAdd}
            selectedIds={[]}
            onSelect={onAddCampaign}
            onPinGroup={onPinGroup}
            onUnpinGroup={onUnpinGroup}
            pinnedWiserIds={pinnedWiserIds}
            placeholder="Add campaign to view stats"
          />
        ) : (
          <p className="text-xs text-muted-foreground">No campaigns available for this country</p>
        )}
      </div>
    );
  }

  const activeGroup = tabGroups[activeStatsTabIndex];
  if (!activeGroup) {
    return (
      <div className="flex min-h-[280px] flex-1 items-center justify-center text-sm text-muted-foreground">
        Select a tab
      </div>
    );
  }

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3 overflow-hidden">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {tabGroups.map((group, i) => {
            const isPinned = group.wiserIds.every((wid) => pinnedWiserIds.includes(wid));
            return (
              <div
                key={group.wiserIds.join(',')}
                className={cn(
                  'group flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-sm transition-colors',
                  i === activeStatsTabIndex
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border bg-background hover:border-muted-foreground/30 hover:bg-muted/40',
                )}
              >
                <button
                  type="button"
                  className="min-w-0 max-w-[280px] truncate font-medium"
                  onClick={() => onTabChange(i)}
                  title={group.fullLabel ?? group.label}
                >
                  {group.label}
                </button>
                {isPinned && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onUnpinGroup(group.wiserIds);
                    }}
                    className="rounded p-0.5 text-muted-foreground opacity-70 hover:bg-muted hover:opacity-100 hover:text-destructive"
                    aria-label={`Unpin ${group.label}`}
                    title="Unpin campaign"
                  >
                    <PinOff className="h-3.5 w-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => onRemoveTabGroup(i)}
                  className="rounded p-0.5 text-muted-foreground opacity-70 hover:bg-muted hover:opacity-100"
                  aria-label={`Remove ${group.label}`}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}
        </div>
        {!phoneOnly && (
          <StatsSourceToggle
            value={statsSource}
            onChange={setStatsSource}
            options={[
              { value: 'tracker', label: statsSourceLabel(activeGroup.trafficProvider) },
              { value: 'tool', label: 'Tool (SMSWiser)' },
            ]}
          />
        )}
        <SmswiserCampaignPicker
          countryKey={countryKey}
          campaigns={availableGroupsToAdd}
          selectedIds={[]}
          onSelect={onAddCampaign}
          onPinGroup={onPinGroup}
          onUnpinGroup={onUnpinGroup}
          pinnedWiserIds={pinnedWiserIds}
          placeholder="Add campaign"
        />
        {canEditTraffic && onEditTrafficLink ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 gap-1.5"
            onClick={onEditTrafficLink}
          >
            <Link2 className="h-3.5 w-3.5" />
            Traffic link
          </Button>
        ) : null}
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-hidden">
        <TrackerStatsScope provider={activeGroup.trafficProvider}>
          {statsSource === 'tool' ? (
            <ToolStatsTab
              countryKey={countryKey}
              wiserIds={activeGroup.wiserIds}
              workDate={workDate}
            />
          ) : (
            <>
              <StatsSubTabBar
                value={statsSubTab}
                onChange={setStatsSubTabWithUrl}
                phoneOnly={phoneOnly}
                trafficProvider={activeGroup.trafficProvider}
                canViewScheduler={canViewScheduler}
                canViewAutomation={canViewAutomation}
              />
              <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                {statsSubTab === 'drops' && (
                  <SmswiserCampaignStatsTab
                    countryKey={countryKey}
                    wiserIds={activeGroup.wiserIds}
                    workDate={workDate}
                    titlePrefix={serverTitlePrefix}
                    perfTagsQuery={perfTagsQuery}
                  />
                )}
                {statsSubTab === 'texts' && (
                  <TextsStatsTab countryKey={countryKey} wiserIds={activeGroup.wiserIds} workDate={workDate} />
                )}
                {statsSubTab === 'lists' && (
                  <ListsStatsTab
                    countryKey={countryKey}
                    wiserIds={activeGroup.wiserIds}
                    workDate={workDate}
                    perfTagsQuery={perfTagsQuery}
                  />
                )}
                {statsSubTab === 'gateways' && (
                  <GatewayStatsTab countryKey={countryKey} wiserIds={activeGroup.wiserIds} workDate={workDate} />
                )}
                {statsSubTab === 'paths' && (
                  <PathsStatsTab
                    countryKey={countryKey}
                    trafficCampaignId={activeGroup.trafficCampaignId}
                    trafficProvider={activeGroup.trafficProvider}
                    wiserIds={activeGroup.wiserIds}
                    workDate={workDate}
                  />
                )}
                {statsSubTab === 'landers' && (
                  <LandersStatsTab
                    countryKey={countryKey}
                    trafficCampaignId={activeGroup.trafficCampaignId}
                    trafficProvider={activeGroup.trafficProvider}
                    wiserIds={activeGroup.wiserIds}
                    workDate={workDate}
                  />
                )}
                {statsSubTab === 'offers' && (
                  <OffersStatsTab
                    countryKey={countryKey}
                    trafficCampaignId={activeGroup.trafficCampaignId}
                    trafficProvider={activeGroup.trafficProvider}
                    wiserIds={activeGroup.wiserIds}
                    workDate={workDate}
                  />
                )}
                {statsSubTab === 'sources' && (
                  <SourcesStatsTab
                    countryKey={countryKey}
                    wiserIds={activeGroup.wiserIds}
                    workDate={workDate}
                    listPerfTagsQuery={perfTagsQuery}
                  />
                )}
                {statsSubTab === 'phone' && (
                  <PhoneStatsTab
                    countryKey={countryKey}
                    wiserIds={activeGroup.wiserIds}
                    workDate={workDate}
                  />
                )}
                {statsSubTab === 'deployed' && <DeployedDropsTab countryKey={countryKey} />}
                {statsSubTab === 'flow' && (
                  <GroupFlowTab
                    trafficProvider={activeGroup.trafficProvider}
                    trafficCampaignId={activeGroup.trafficCampaignId}
                  />
                )}
                {statsSubTab === 'scheduler' && canViewScheduler && (
                  <SchedulerTab countryKey={countryKey} />
                )}
                {statsSubTab === 'automation' && canViewAutomation && (
                  <AutomationTab countryKey={countryKey} campaignWiserId={activeGroup.wiserIds[0]} />
                )}
              </div>
            </>
          )}
        </TrackerStatsScope>
      </div>
    </div>
  );
};
